import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from "@/components/ui/dialog";

import { Search, Download } from "lucide-react";
import { toast } from "sonner";
import { inviteService } from "@/services/invite";
import { DateUtils } from "@/utils/dateUtils";
import { SortIcon } from "@/components/ui/sort-icon";

// 类型
type QueryField = "invitation_code" | "invitation_phone";

type Invitation = {
  id: string;
  inviterPhone?: string | null; // 邀请人手机号
  invitationCode: string; // 邀请码
  inviteePhone?: string | null; // 被邀请人手机号
  inviteeRegisterTime: string; // 被邀请人注册时间
};

export default function InvitationManagement() {
  // 分页
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);

  // 数据
  const [loading, setLoading] = useState(true);
  // 保存原始数据
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  // 保存排序过的数据
  const [sortedInvitations,setSortedInvitations] = useState<Invitation[]>([])
  const [total, setTotal] = useState(0);

  // 1) 条件查询：字段 + 输入
  const [field, setField] = useState<QueryField>("invitation_code");
  const [keyword, setKeyword] = useState("");

  // 2) 被邀请人注册时间筛选
  const [timeStart, setTimeStart] = useState<string>("");
  const [timeEnd, setTimeEnd] = useState<string>("");

  // 3) 被邀请人手机号
  const [inviteePhone, setInviteePhone] = useState<string>("");

  // 导出弹窗相关状态
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [processingExport, setProcessingExport] = useState(false);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / pageSize)),
    [total, pageSize]
  );

  // UI状态 -> 接口参数映射
  const buildRequestParams = () => {
    const params: any = {
      page,
      page_size: pageSize,
    };

    // 条件查询：根据选中的字段名赋值
    const kw = keyword.trim();
    if (kw) {
      if (field === "invitation_code") {
        params.invitation_code = kw;
      } else if (field === "invitation_phone") {
        params.invitation_phone = kw;
      }
    }

    // 被邀请人注册时间筛选
    if (timeStart) {
      params.start_time = DateUtils.toChineseISOString(timeStart, false);
    }
    if (timeEnd) {
      params.end_time = DateUtils.toChineseISOString(timeEnd, true);
    }

    // 被邀请人手机号
    if (inviteePhone.trim()) {
      params.phone = inviteePhone.trim();
    }

    return params;
  };

  const fetchInvitations = async () => {
    setLoading(true);
    try {
      const params = buildRequestParams();
      const res = await inviteService.getInvitations(params);

      // 更新映射逻辑以匹配API返回的结构
      const list = (res as any).list ?? [];
      const totalCount = (res as any).total ?? 0;
      const invitationList = list.map((item: any) => ({
        id: item.id,
        inviterPhone: item.invitationPhone, // 邀请人手机号字段名为 invitationPhone
        invitationCode: item.invitationCode, // 邀请码字段名正确
        inviteePhone: item.phone, // 被邀请人手机号字段名为 phone
        inviteeRegisterTime: item.create_at, // 被邀请人注册时间字段名为 create_at
      }))

      setInvitations(invitationList);
      setSortedInvitations(invitationList)
      setTotal(totalCount);
    } catch (e) {
      console.error(e);
      toast.error("获取邀请列表失败", {
        description: e instanceof Error ? e.message : "请稍后重试",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvitations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  const handleSearch = () => {
    setPage(1);
    fetchInvitations();
  };

  const resetFilters = () => {
    setKeyword("");
    setTimeStart("");
    setTimeEnd("");
    setInviteePhone("");
    setField("invitation_code");
    setPage(1);
  };

  // 打开导出确认弹窗
  const handleExport = () => {
    setExportDialogOpen(true);
  };

  // 执行实际导出操作
  const confirmExport = async () => {
    setProcessingExport(true);
    try {
      // 构建请求参数，与 getInvitations 使用相同的参数结构
      const params = buildRequestParams();

      // 调用 inviteService 的 exportInvitations 方法
      const blob = await inviteService.exportInvitations(params);

      // 创建下载链接
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `邀请记录_${new Date().toISOString().split("T")[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();

      // 清理
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success("导出成功", {
        description: "邀请记录已成功导出",
      });

      // 关闭弹窗
      setExportDialogOpen(false);
    } catch (e) {
      console.error(e);
      toast.error("导出失败", {
        description: e instanceof Error ? e.message : "请稍后重试",
      });
    } finally {
      setProcessingExport(false);
    }
  };

  const placeholderByField: Record<QueryField, string> = {
    invitation_code: "请输入邀请码",
    invitation_phone: "请输入邀请人手机号",
  };

  const renderFilterSummary = () => {
    if (field === "invitation_code" && keyword) return `邀请码: ${keyword}`;
    if (field === "invitation_phone" && keyword)
      return `邀请人手机号: ${keyword}`;
    return "无特定条件";
  };
  const onSortStateChange = (newInvitations: Invitation[]) => {
    setSortedInvitations(newInvitations)
  }

  return (
    <div className="space-y-4 h-full flex flex-col">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold tracking-tight">邀请管理</h2>
      </div>

      {/* 筛选区 - 现在分为两行 */}
      <div className="rounded-lg border p-4 space-y-4">
        {/* 第一行 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* 1. 条件查询 */}
          <div className="flex flex-col gap-2">
            <label className="text-sm text-gray-600">条件查询</label>
            <div className="flex gap-2">
              <Select
                value={field}
                onValueChange={(v: QueryField) => setField(v)}
              >
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="选择字段" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="invitation_code">邀请码</SelectItem>
                  <SelectItem value="invitation_phone">邀请人手机号</SelectItem>
                </SelectContent>
              </Select>
              <Input
                placeholder={placeholderByField[field]}
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                className="flex-1"
              />
            </div>
          </div>

          {/* 2. 被邀请人注册时间 */}
          <div className="flex flex-col gap-2">
            <label className="text-sm text-gray-600">被邀请人注册时间</label>
            <div className="flex gap-2">
              <Input
                type="date"
                value={timeStart}
                onChange={(e) => setTimeStart(e.target.value)}
              />
              <Input
                type="date"
                value={timeEnd}
                onChange={(e) => setTimeEnd(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* 第二行 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* 3. 被邀请人手机号 */}
          <div className="flex flex-col gap-2">
            <label className="text-sm text-gray-600">被邀请人手机号</label>
            <div className="flex gap-2">
              <Input
                placeholder="请输入被邀请人手机号"
                value={inviteePhone}
                onChange={(e) => setInviteePhone(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                className="flex-1"
              />
            </div>
          </div>

          {/* 4. 操作按钮组 */}
          <div className="flex flex-col gap-2 justify-end">
            <label className="text-sm text-gray-600 opacity-0">操作</label>
            <div className="flex gap-2 justify-end">
              <Button className="w-24" variant="outline" onClick={resetFilters}>
                重置
              </Button>
              <Button className="w-24" onClick={handleSearch}>
                <Search className="mr-2 h-4 w-4" />
                查询
              </Button>
              <Button
                className="w-24"
                variant="secondary"
                onClick={handleExport}
              >
                <Download className="mr-2 h-4 w-4" />
                导出
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* 列表：可滚动容器 */}
      <div className="rounded-md border overflow-auto max-h-[60vh]">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="whitespace-nowrap">邀请人手机号</TableHead>
              <TableHead className="whitespace-nowrap">邀请码</TableHead>
              <TableHead className="whitespace-nowrap">
                被邀请人手机号
              </TableHead>
              <TableHead className="whitespace-nowrap">
                <SortIcon
                  text="被邀请人注册时间" 
                  sortItem="inviteeRegisterTime"
                  resources={invitations}  
                  onSortStateChange={onSortStateChange}
                />
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} className="h-24 text-center">
                  <div className="flex justify-center items-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
                  </div>
                </TableCell>
              </TableRow>
            ) : invitations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="h-24 text-center">
                  没有找到邀请记录
                </TableCell>
              </TableRow>
            ) : (
              sortedInvitations.map((invitation) => (
                <TableRow key={invitation.id}>
                  <TableCell className="whitespace-nowrap">
                    {invitation.inviterPhone || "-"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {invitation.invitationCode}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {invitation.inviteePhone || "-"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {invitation.inviteeRegisterTime
                      ? new Date(
                          invitation.inviteeRegisterTime
                        ).toLocaleString()
                      : "-"}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* 导出确认弹窗 */}
      <Dialog
        open={exportDialogOpen}
        onOpenChange={(open) => !open && setExportDialogOpen(false)}
      >
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>确认导出邀请记录</DialogTitle>
            <DialogDescription>
              将当前筛选条件下的邀请记录导出为Excel文件
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">导出记录数量:</span>
                <span className="font-medium text-blue-600">{total}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">筛选条件:</span>
                <span className="text-gray-600">{renderFilterSummary()}</span>
              </div>
              {timeStart && (
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">开始时间:</span>
                  <span className="text-gray-600">{timeStart}</span>
                </div>
              )}
              {timeEnd && (
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">结束时间:</span>
                  <span className="text-gray-600">{timeEnd}</span>
                </div>
              )}
              {inviteePhone && (
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">被邀请人手机号:</span>
                  <span className="text-gray-600">{inviteePhone}</span>
                </div>
              )}
            </div>

            <p className="text-sm text-gray-500">
              导出的Excel文件将包含所有当前筛选条件下的邀请记录数据
            </p>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <DialogClose asChild>
              <Button variant="outline">取消</Button>
            </DialogClose>
            <Button onClick={confirmExport} disabled={processingExport}>
              {processingExport ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-t-transparent"></div>
                  导出中...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  确认导出
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {!loading && invitations.length > 0 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-500 whitespace-nowrap">
            共 {total} 条邀请记录，第 {page} 页 / 共 {totalPages} 页
          </div>
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => page > 1 && setPage(page - 1)}
                  className={page <= 1 ? "pointer-events-none opacity-50" : ""}
                />
              </PaginationItem>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) pageNum = i + 1;
                else if (page <= 3) pageNum = i + 1;
                else if (page >= totalPages - 2) pageNum = totalPages - 4 + i;
                else pageNum = page - 2 + i;
                return (
                  <PaginationItem key={i}>
                    <PaginationLink
                      isActive={page === pageNum}
                      onClick={() => setPage(pageNum)}
                    >
                      {pageNum}
                    </PaginationLink>
                  </PaginationItem>
                );
              })}
              <PaginationItem>
                <PaginationNext
                  onClick={() => page < totalPages && setPage(page + 1)}
                  className={
                    page >= totalPages ? "pointer-events-none opacity-50" : ""
                  }
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
}
